import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import "../styles/Purchases.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserPlus,
    faUser,
    faTag,
    faPhone,
    faEnvelope,
    faCalendar,
    faMapMarkerAlt,
    faSave,
    faTimes,
    faSpinner,
    faCheckCircle,
    faExclamationTriangle,
    faStore,
    faExchangeAlt,
    faBriefcase,
    faBuilding,
    faMoneyBillWave,
    faClock,
    faPercentage,
    faUniversity,
    faCreditCard,
    faIdCard,
    faPhoneAlt,
    faUserTag,
    faChevronDown,
    faHistory,
    faEdit,
    faTrash,
    faEye,
    faSearch,
    faFilter,
    faChartLine,
    faAddressCard,
    faDollarSign,
    faReceipt
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// Translation Utility
// ==============================================
const createEmployeeTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);
    let translationListeners = new Set();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_employee_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Employee Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_employee_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        addEmployee: 'Add Employee',
        editEmployee: 'Edit Employee',
        employeeDetails: 'Employee Details',
        employeeName: 'Employee Name',
        phoneNumber: 'Phone Number',
        email: 'Email',
        address: 'Address',
        position: 'Position',
        department: 'Department',
        joiningDate: 'Joining Date',
        salaryType: 'Salary Type',
        baseSalary: 'Base Salary',
        hourlyRate: 'Hourly Rate',
        commissionRate: 'Commission Rate (%)',
        bankName: 'Bank Name',
        accountNumber: 'Account Number',
        ifscCode: 'IFSC Code',
        panNumber: 'PAN Number',
        aadharNumber: 'Aadhar Number',
        emergencyContact: 'Emergency Contact Name',
        emergencyPhone: 'Emergency Phone',
        status: 'Status',
        selectStatus: 'Select status',
        active: 'Active',
        inactive: 'Inactive',
        terminated: 'Terminated',
        fixed: 'Fixed',
        hourly: 'Hourly',
        commission: 'Commission',
        saveEmployee: 'Save Employee',
        updateEmployee: 'Update Employee',
        clearButton: 'Clear',
        adding: 'Adding...',
        updating: 'Updating...',
        employeeAdded: 'Employee saved successfully!',
        employeeUpdated: 'Employee updated successfully!',
        employeeDeleted: 'Employee deleted successfully!',
        failedAdd: 'Failed to save employee',
        failedUpdate: 'Failed to update employee',
        failedDelete: 'Failed to delete employee',
        networkError: 'Network error occurred',
        nameRequired: 'Employee name is required',
        nameTooShort: 'Name must be at least 2 characters',
        phoneInvalid: 'Please enter a valid phone number',
        emailInvalid: 'Please enter a valid email address',
        dateInvalid: 'Please select a valid date',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        selectShop: 'Select a shop first',
        currentShop: 'Current Shop',
        willAddToShop: 'Employee will be added to this shop',
        noShopSelected: 'No shop selected. Please switch to a shop first.',
        switchShop: 'Switch Shop',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        noShopPermission: 'You do not have permission to add employees to this shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        fillDetails: 'Fill in the employee details below',
        history: 'History',
        changeStatus: 'Change Status',
        reason: 'Reason',
        additionalNotes: 'Additional Notes',
        cancel: 'Cancel',
        ok: 'OK',
        deleteConfirm: 'Are you sure you want to delete this employee?',
        deleteWarning: 'This action cannot be undone.',
        totalEmployees: 'Total Employees',
        activeEmployees: 'Active Employees',
        inactiveEmployees: 'Inactive Employees',
        terminatedEmployees: 'Terminated Employees',
        by: 'By',
        currentStatus: 'Current Status',
        newStatus: 'New Status',
        statusChanged: 'Status changed successfully!',
        statusUpdateFailed: 'Failed to update status'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    const addListener = (callback) => {
        translationListeners.add(callback);
    };

    const removeListener = (callback) => {
        translationListeners.delete(callback);
    };

    const notifyListeners = (lang) => {
        translationListeners.forEach(callback => callback(lang));
    };

    const checkLanguageChange = () => {
        const currentLang = localStorage.getItem('ourzest_language') || 'en';
        notifyListeners(currentLang);
    };

    const startLanguageCheck = () => {
        checkLanguageChange();
        const intervalId = setInterval(checkLanguageChange, 500);
        window.addEventListener('storage', checkLanguageChange);
        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', checkLanguageChange);
        };
    };

    loadFromCache();

    return {
        getTranslations,
        translateText,
        addListener,
        removeListener,
        startLanguageCheck
    };
};

const employeeTranslator = createEmployeeTranslator();

// ==============================================
// Toast Notification Component
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations?.success || 'Success';
            case 'error':
                return translations?.error || 'Error';
            case 'warning':
                return translations?.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Business Info Component
// ==============================================
const BusinessInfo = ({ businessInfo, translations }) => {
    if (!businessInfo) return null;

    return (
        <div className="business-info-card" style={{ 
            background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
            borderRadius: '12px',
            // padding: '15px',
            // marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            // gap: '15px'
        }}>
            {businessInfo.bussinesslogo && (
                <img 
                    src={businessInfo.bussinesslogo} 
                    alt="Logo" 
                    style={{ 
                        width: '50px', 
                        height: '50px', 
                        objectFit: 'contain',
                        borderRadius: '8px'
                    }}
                />
            )}
            <div className="business-details">
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>
                    {businessInfo.businessname}
                </div>
                {businessInfo.businessaddress && (
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} size="xs" style={{ marginRight: '5px' }} />
                        {businessInfo.businessaddress}
                    </div>
                )}
                {businessInfo.phonenumber && (
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                        <FontAwesomeIcon icon={faPhone} size="xs" style={{ marginRight: '5px' }} />
                        {businessInfo.phonenumber}
                    </div>
                )}
            </div>
        </div>
    );
};

// ==============================================
// Summary Cards Component
// ==============================================
const SummaryCards = ({ summary, translations, isEditMode }) => {
    if (!summary || isEditMode) return null;

    const formatNumber = (num) => {
        return new Intl.NumberFormat('en-US').format(num || 0);
    };

    return (
        <div className="summary-section" style={{ marginBottom: '20px' }}>
            <div className="summary-cards" style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '15px',
                marginBottom: '20px'
            }}>
                <div className="summary-card" style={{ 
                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                    color: 'white',
                    padding: '15px',
                    borderRadius: '10px'
                }}>
                    <div style={{ fontSize: '12px', opacity: '0.9' }}>{translations?.totalEmployees || 'Total Employees'}</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                        {formatNumber(summary.total || 0)}
                    </div>
                </div>
                
                <div className="summary-card" style={{ 
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    padding: '15px',
                    borderRadius: '10px'
                }}>
                    <div style={{ fontSize: '12px', opacity: '0.9' }}>{translations?.activeEmployees || 'Active Employees'}</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                        {formatNumber(summary.active || 0)}
                    </div>
                </div>

                <div className="summary-card" style={{ 
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    color: 'white',
                    padding: '15px',
                    borderRadius: '10px'
                }}>
                    <div style={{ fontSize: '12px', opacity: '0.9' }}>{translations?.inactiveEmployees || 'Inactive Employees'}</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                        {formatNumber(summary.inactive || 0)}
                    </div>
                </div>

                <div className="summary-card" style={{ 
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    color: 'white',
                    padding: '15px',
                    borderRadius: '10px'
                }}>
                    <div style={{ fontSize: '12px', opacity: '0.9' }}>{translations?.terminatedEmployees || 'Terminated Employees'}</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                        {formatNumber(summary.terminated || 0)}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Status History Modal
// ==============================================
const StatusHistoryModal = ({ employeeId, shopId, onClose, translations }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadHistory();
    }, [employeeId]);

    const loadHistory = async () => {
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/employees.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_history',
                    id: employeeId,
                    shop_id: shopId
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setHistory(data.data);
            } else {
                setError(data.message);
            }
        } catch (error) {
            setError(translations?.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    const getStatusColor = (status) => {
        const colors = {
            'active': '#10b981',
            'inactive': '#f59e0b',
            'terminated': '#ef4444'
        };
        return colors[status] || '#64748b';
    };

    if (!employeeId) return null;

    return (
        <div className="modal-overlay-modern" onClick={onClose}>
            <div className="modal-content-modern" onClick={e => e.stopPropagation()}>
                <div className="modal-header-modern">
                    <h3>
                        <FontAwesomeIcon icon={faHistory} />
                        {translations?.history || 'Status History'}
                    </h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="modal-body-modern">
                    {loading ? (
                        <div className="modal-loading">
                            <div className="loading-spinner"></div>
                            <p>{translations?.loading || 'Loading...'}</p>
                        </div>
                    ) : error ? (
                        <div className="modal-error">
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            <p>{error}</p>
                        </div>
                    ) : history.length === 0 ? (
                        <div className="modal-empty">
                            <FontAwesomeIcon icon={faHistory} />
                            <p>{translations?.noHistory || 'No status history found'}</p>
                        </div>
                    ) : (
                        <div className="history-timeline">
                            {history.map((item, index) => (
                                <div key={item.id || index} className="history-item">
                                    <div className="history-status-badge">
                                        <span className="old-status" style={{ background: getStatusColor(item.old_status) }}>
                                            {item.old_status || 'N/A'}
                                        </span>
                                        <span className="status-arrow">→</span>
                                        <span className="new-status" style={{ background: getStatusColor(item.new_status) }}>
                                            {item.new_status}
                                        </span>
                                    </div>
                                    <div className="history-date">
                                        {formatDate(item.changed_at)}
                                    </div>
                                    {item.reason && (
                                        <div className="history-reason">
                                            <strong>{translations?.reason || 'Reason'}:</strong> {item.reason}
                                        </div>
                                    )}
                                    {item.notes && (
                                        <div className="history-notes">
                                            <strong>{translations?.additionalNotes || 'Notes'}:</strong> {item.notes}
                                        </div>
                                    )}
                                    {item.changed_by_username && (
                                        <div className="history-user">
                                            <strong>{translations?.by || 'By'}:</strong> {item.changed_by_username}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Status Change Modal
// ==============================================
const StatusChangeModal = ({ employee, shopId, onClose, onStatusChanged, translations }) => {
    const [status, setStatus] = useState(employee?.status || '');
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const statusOptions = [
        { value: 'active', label: 'active', icon: faCheckCircle },
        { value: 'inactive', label: 'inactive', icon: faClock },
        { value: 'terminated', label: 'terminated', icon: faTimesCircle }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!status) {
            setError(translations?.selectStatus || 'Please select a status');
            return;
        }

        if (status === employee?.status) {
            setError(translations?.statusDifferent || 'New status must be different');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const username = localStorage.getItem('username') || employee?.username;
            
            const payload = {
                action: 'change_status',
                id: employee.id,
                status: status,
                username: username,
                shop_id: shopId,
                reason: reason || null,
                notes: notes || null
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/employees.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                if (onStatusChanged) {
                    onStatusChanged(data.data);
                }
                onClose();
            } else {
                setError(data.message || translations?.statusUpdateFailed || 'Failed to update status');
            }
        } catch (error) {
            setError(translations?.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    if (!employee) return null;

    return (
        <div className="modal-overlay-modern" onClick={onClose}>
            <div className="modal-content-modern" onClick={e => e.stopPropagation()}>
                <div className="modal-header-modern">
                    <h3>
                        <FontAwesomeIcon icon={faExchangeAlt} />
                        {translations?.changeStatus || 'Change Status'}
                    </h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="modal-body-modern">
                        {error && (
                            <div className="modal-error">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                {error}
                            </div>
                        )}

                        <div className="current-status-display">
                            <label>{translations?.currentStatus || 'Current Status'}:</label>
                            <span className="status-badge" style={{ background: getStatusColor(employee?.status) }}>
                                {employee?.status}
                            </span>
                        </div>

                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faTag} className="input-icon" />
                                <select
                                    id="status"
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    disabled={loading}
                                    required
                                >
                                    <option value="">{translations?.selectStatus || 'Select Status'}</option>
                                    {statusOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {translations?.[option.label] || option.value}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="status" className="input-label">
                                    {translations?.newStatus || 'New Status'} <span className="required">*</span>
                                </label>
                            </div>
                        </div>

                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faFileAlt} className="input-icon" />
                                <input
                                    type="text"
                                    id="reason"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder=" "
                                    disabled={loading}
                                />
                                <label htmlFor="reason" className="input-label">
                                    {translations?.reason || 'Reason for change'}
                                </label>
                            </div>
                        </div>

                        <div className="form-group-modern">
                            <div className="input-container textarea-container">
                                <FontAwesomeIcon icon={faReceipt} className="input-icon" />
                                <textarea
                                    id="notes"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder=" "
                                    disabled={loading}
                                    rows="3"
                                ></textarea>
                                <label htmlFor="notes" className="input-label">
                                    {translations?.additionalNotes || 'Additional Notes'}
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer-modern">
                        <button
                            type="button"
                            className="btn-modern btn-secondary-modern"
                            onClick={onClose}
                            disabled={loading}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                            <span>{translations?.cancel || 'Cancel'}</span>
                        </button>
                        <button
                            type="submit"
                            className="btn-modern btn-primary-modern"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <div className="btn-spinner"></div>
                                    <span>{translations?.updating || 'Updating...'}</span>
                                </>
                            ) : (
                                <>
                                    <span>{translations?.update || 'Update Status'}</span>
                                    <FontAwesomeIcon icon={faSave} />
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Helper function for status colors
const getStatusColor = (status) => {
    const colors = {
        'active': '#10b981',
        'inactive': '#f59e0b',
        'terminated': '#ef4444'
    };
    return colors[status] || '#64748b';
};

// ==============================================
// Shop Selector Modal Component
// ==============================================
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content shop-selector-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                        {shop.shop_role && (
                                            <div className="shop-item-role">Role: {shop.shop_role}</div>
                                        )}
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Main Employee Component
// ==============================================
const AddEmployee = ({ employeeId = null, onSuccess = null }) => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();

    const [shopId, setShopId] = useState(() => {
        if (user?.shop_id) {
            return user.shop_id;
        }
        return localStorage.getItem('current_shop_id') || '';
    });

    const [shopInfo, setShopInfo] = useState({
        id: shopId,
        name: currentShop?.shop_name || localStorage.getItem('current_shop_name') || ''
    });

    const [businessInfo, setBusinessInfo] = useState(null);
    const [summary, setSummary] = useState(null);

    const [formData, setFormData] = useState({
        employee_name: '',
        phone: '',
        email: '',
        address: '',
        position: '',
        department: '',
        joining_date: new Date().toISOString().split('T')[0],
        salary_type: 'fixed',
        base_salary: '',
        hourly_rate: '',
        commission_rate: '',
        bank_name: '',
        account_number: '',
        ifsc_code: '',
        pan_number: '',
        aadhar_number: '',
        emergency_contact: '',
        emergency_phone: '',
        status: 'active',
        themeColor: localStorage.getItem('ourzest_theme') || '#8b5cf6'
    });

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [isTranslating, setIsTranslating] = useState(false);
    
    // Modal states
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    
    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Refs
    const toastTimerRef = useRef(null);
    const isSubmittingRef = useRef(false);
    const submissionLockRef = useRef(false);
    const lastSubmissionRef = useRef(null);
    const formRef = useRef(null);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    // Salary type options
    const salaryTypeOptions = [
        { value: 'fixed', label: 'fixed', displayName: 'Fixed Salary' },
        { value: 'hourly', label: 'hourly', displayName: 'Hourly Rate' },
        { value: 'commission', label: 'commission', displayName: 'Commission Based' }
    ];

    // Status options
    const statusOptions = [
        { value: 'active', label: 'active', displayName: 'Active' },
        { value: 'inactive', label: 'inactive', displayName: 'Inactive' },
        { value: 'terminated', label: 'terminated', displayName: 'Terminated' }
    ];

    // Show toast function
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    }, [currentLanguage]);

    // Clean up timer
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
            isSubmittingRef.current = false;
            submissionLockRef.current = false;
        };
    }, []);

    // Load translations
    const loadTranslations = useCallback(async (lang) => {
        if (!lang) return;

        setIsTranslating(true);
        try {
            const trans = await employeeTranslator.getTranslations(lang);
            setTranslations(trans);

            const isRTL = lang === 'ur' || lang === 'ar';
            document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
            document.body.dir = isRTL ? 'rtl' : 'ltr';

        } catch (error) {
            console.error('Error loading translations:', error);
        } finally {
            setIsTranslating(false);
        }
    }, []);

    // Setup language change listener
    useEffect(() => {
        loadTranslations(currentLanguage);

        const handleLanguageChange = () => {
            const newLang = localStorage.getItem('ourzest_language') || 'en';
            if (newLang !== currentLanguage) {
                setCurrentLanguage(newLang);
                loadTranslations(newLang);
            }
        };

        window.addEventListener('storage', handleLanguageChange);

        const intervalId = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 500);

        const translationListener = (lang) => {
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        employeeTranslator.addListener(translationListener);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(intervalId);
            employeeTranslator.removeListener(translationListener);
        };
    }, [currentLanguage, loadTranslations]);

    // Load business info
    const loadBusinessInfo = useCallback(async () => {
        if (!user?.username) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo(data);
            }
        } catch (error) {
            console.error('Error loading business info:', error);
        }
    }, [user?.username]);

    // Load summary
    const loadSummary = useCallback(async () => {
        if (!shopId) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/employees.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_summary',
                    shop_id: shopId
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setSummary(data.data);
            }
        } catch (error) {
            console.error('Error loading summary:', error);
        }
    }, [shopId]);

    // Load employee data if in edit mode
    useEffect(() => {
        if (employeeId) {
            setIsEditMode(true);
            loadEmployeeData(employeeId);
        }
    }, [employeeId]);

    // Load data on mount
    useEffect(() => {
        loadBusinessInfo();
        if (!employeeId) {
            loadSummary();
        }
    }, [shopId, employeeId, loadBusinessInfo, loadSummary]);

    // Update shopInfo when currentShop changes
    useEffect(() => {
        if (currentShop) {
            setShopInfo({
                id: currentShop.shop_id,
                name: currentShop.shop_name
            });
        }
    }, [currentShop]);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'purchase-primary-color': color,
                    'purchase-primary-dark': adjust(-40),
                    'purchase-primary-light': adjust(40),
                    'purchase-primary-ultralight': adjust(20),
                    'purchase-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'purchase-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(formData.themeColor);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            if (theme !== formData.themeColor) {
                setFormData(prev => ({ ...prev, themeColor: theme }));
                applyTheme(theme);
            }
        };

        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [formData.themeColor]);

    // Load available shops
    useEffect(() => {
        const loadAvailableShops = async () => {
            if (!user) return;

            setLoadingShops(true);
            try {
                const shopsData = await getShopsForSwitch();
                setAvailableShops(shopsData.shops || []);
            } catch (error) {
                console.error('Error loading shops:', error);
                setAvailableShops([]);
            } finally {
                setLoadingShops(false);
            }
        };

        loadAvailableShops();
    }, [user, getShopsForSwitch]);

    const loadEmployeeData = async (id) => {
        if (!shopId) return;

        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/employees.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_record',
                    id: id,
                    shop_id: shopId
                })
            });

            const data = await response.json();
            if (data.status === 'success' && data.data) {
                const employee = data.data;
                setFormData(prev => ({
                    ...prev,
                    employee_name: employee.employee_name || '',
                    phone: employee.phone || '',
                    email: employee.email || '',
                    address: employee.address || '',
                    position: employee.position || '',
                    department: employee.department || '',
                    joining_date: employee.joining_date || new Date().toISOString().split('T')[0],
                    salary_type: employee.salary_type || 'fixed',
                    base_salary: employee.base_salary || '',
                    hourly_rate: employee.hourly_rate || '',
                    commission_rate: employee.commission_rate || '',
                    bank_name: employee.bank_name || '',
                    account_number: employee.account_number || '',
                    ifsc_code: employee.ifsc_code || '',
                    pan_number: employee.pan_number || '',
                    aadhar_number: employee.aadhar_number || '',
                    emergency_contact: employee.emergency_contact || '',
                    emergency_phone: employee.emergency_phone || '',
                    status: employee.status || 'active'
                }));
            } else {
                showToast('error', data.message || translations?.failedLoad || 'Failed to load employee data');
            }
        } catch (error) {
            showToast('error', translations?.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.employee_name.trim()) {
            newErrors.employee_name = translations.nameRequired || 'Employee name is required';
        } else if (formData.employee_name.trim().length < 2) {
            newErrors.employee_name = translations.nameTooShort || 'Name must be at least 2 characters';
        }

        if (formData.phone && !/^[\d\s\-\+\(\)]{10,15}$/.test(formData.phone)) {
            newErrors.phone = translations.phoneInvalid || 'Please enter a valid phone number';
        }

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = translations.emailInvalid || 'Please enter a valid email address';
        }

        if (formData.joining_date && !/^\d{4}-\d{2}-\d{2}$/.test(formData.joining_date)) {
            newErrors.joining_date = translations.dateInvalid || 'Please select a valid date';
        }

        // Validate based on salary type
        if (formData.salary_type === 'fixed' && !formData.base_salary) {
            newErrors.base_salary = 'Base salary is required for fixed salary';
        }
        if (formData.salary_type === 'hourly' && !formData.hourly_rate) {
            newErrors.hourly_rate = 'Hourly rate is required';
        }
        if (formData.salary_type === 'commission' && !formData.commission_rate) {
            newErrors.commission_rate = 'Commission rate is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleClear = () => {
        setFormData({
            employee_name: '',
            phone: '',
            email: '',
            address: '',
            position: '',
            department: '',
            joining_date: new Date().toISOString().split('T')[0],
            salary_type: 'fixed',
            base_salary: '',
            hourly_rate: '',
            commission_rate: '',
            bank_name: '',
            account_number: '',
            ifsc_code: '',
            pan_number: '',
            aadhar_number: '',
            emergency_contact: '',
            emergency_phone: '',
            status: 'active',
            themeColor: formData.themeColor
        });
        setErrors({});
        setIsEditMode(false);
        lastSubmissionRef.current = null;
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(
                shop.shop_id,
                shop.shop_name,
                shop.shop_code
            );

            if (success) {
                showToast('success', translations.shopSwitched || 'Shop switched successfully');
                loadSummary();
            } else {
                showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
        }
    };

    const handleViewHistory = () => {
        setShowHistoryModal(true);
    };

    const handleChangeStatus = () => {
        setShowStatusModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!shopId) {
            showToast('error', translations.noShopSelected || 'No shop selected. Please select a shop first.');
            setShowShopSelector(true);
            return;
        }

        if (!validateForm()) {
            const firstError = document.querySelector('.has-error, .error-message');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        if (isSubmittingRef.current || submissionLockRef.current) {
            console.log('🚫 Submission blocked - already in progress');
            return;
        }

        const now = Date.now();
        if (lastSubmissionRef.current) {
            const timeDiff = now - lastSubmissionRef.current.time;
            const dataMatch = JSON.stringify(formData) === lastSubmissionRef.current.data;

            if (timeDiff < 3000 && dataMatch) {
                console.log('🚫 Duplicate submission prevented (client-side)');
                showToast('warning', 'Please wait before submitting again');
                return;
            }
        }

        isSubmittingRef.current = true;
        submissionLockRef.current = true;
        setSaving(true);

        lastSubmissionRef.current = {
            time: now,
            data: JSON.stringify(formData)
        };

        try {
            const currentUsername = user?.username || localStorage.getItem('username');
            if (!currentUsername) {
                throw new Error("User not authenticated");
            }

            const action = isEditMode ? 'edit' : 'add';

            const payload = {
                action: action,
                username: currentUsername,
                shop_id: shopId,
                ...formData,
                submission_timestamp: now
            };

            if (isEditMode && employeeId) {
                payload.id = employeeId;
            }

            const response = await fetch('https://ourzestapp.online/oz_backend/employees.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                const successMessage = isEditMode 
                    ? (translations.employeeUpdated || 'Employee updated successfully!')
                    : (translations.employeeAdded || 'Employee saved successfully!');
                
                showToast('success', successMessage);

                if (!isEditMode) {
                    handleClear();
                    loadSummary();
                }

                setErrors({});

                if (onSuccess) {
                    onSuccess(data.data?.id || employeeId);
                }

            } else {
                if (data.duplicate_prevention || data.duplicate_database) {
                    showToast('warning', data.message || 'Duplicate submission detected');
                } else {
                    showToast('error', data.message || (isEditMode ? translations.failedUpdate : translations.failedAdd) || 'Failed to save employee');
                }
            }
        } catch (error) {
            showToast('error', error.message || translations.networkError || 'Network error occurred');
        } finally {
            setTimeout(() => {
                isSubmittingRef.current = false;
                submissionLockRef.current = false;
                setSaving(false);
            }, 1000);
        }
    };

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faExchangeAlt} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }
        return null;
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`purchases-form-independent ${isRTL ? 'rtl' : ''} ${isTranslating ? 'translating' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Modern Form Header */}
            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={isEditMode ? faEdit : faUserPlus} />
                    </div>
                    <div className="header-text">
                        <h1>{isEditMode ? (translations.editEmployee || 'Edit Employee') : (translations.addEmployee || 'Add Employee')}</h1>
                        <p>{translations.fillDetails || 'Fill in the employee details below'}</p>
                    </div>
                </div>
                {isEditMode && (
                    <div className="header-actions">
                        <button
                            type="button"
                            className="btn-icon"
                            onClick={handleViewHistory}
                            title={translations.history || 'History'}
                        >
                            <FontAwesomeIcon icon={faHistory} />
                        </button>
                        <button
                            type="button"
                            className="btn-icon"
                            onClick={handleChangeStatus}
                            title={translations.changeStatus || 'Change Status'}
                        >
                            <FontAwesomeIcon icon={faExchangeAlt} />
                        </button>
                    </div>
                )}
                {renderShopIndicator()}
                {isTranslating && (
                    <div className="translation-indicator">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Translating...</span>
                    </div>
                )}
            </div>

            {/* Business Info */}
            <BusinessInfo businessInfo={businessInfo} translations={translations} />

            {/* Summary Cards */}
            <SummaryCards summary={summary} translations={translations} isEditMode={isEditMode} />

            {/* Modern Form - Single Section with All Fields */}
            <form ref={formRef} onSubmit={handleSubmit} className="modern-form">
                <div className="form-section">
                    <div className="form-grid-modern">
                        {/* Employee Name - Required */}
                        <div className={`form-group-modern ${errors.employee_name ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faUser} className="input-icon" />
                                <input
                                    type="text"
                                    id="employee_name"
                                    name="employee_name"
                                    value={formData.employee_name}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                    className={errors.employee_name ? 'error' : ''}
                                />
                                <label htmlFor="employee_name" className="input-label">
                                    {translations.employeeName || 'Employee Name'} <span className="required">*</span>
                                </label>
                                {errors.employee_name && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.employee_name}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Phone Number */}
                        <div className={`form-group-modern ${errors.phone ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faPhone} className="input-icon" />
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir="ltr"
                                    className={errors.phone ? 'error' : ''}
                                />
                                <label htmlFor="phone" className="input-label">
                                    {translations.phoneNumber || 'Phone Number'}
                                </label>
                                {errors.phone && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.phone}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Email */}
                        <div className={`form-group-modern ${errors.email ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                                <input
                                    type="text"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir="ltr"
                                    className={errors.email ? 'error' : ''}
                                />
                                <label htmlFor="email" className="input-label">
                                    {translations.email || 'Email'}
                                </label>
                                {errors.email && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.email}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Joining Date */}
                        <div className={`form-group-modern ${errors.joining_date ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCalendar} className="input-icon" />
                                <input
                                    type="date"
                                    id="joining_date"
                                    name="joining_date"
                                    value={formData.joining_date}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir="ltr"
                                    placeholder=" "
                                    className={errors.joining_date ? 'error' : ''}
                                />
                                <label htmlFor="joining_date" className="input-label">
                                    {translations.joiningDate || 'Joining Date'}
                                </label>
                                {errors.joining_date && (
                                    <div className="error-message">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors.joining_date}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Position */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faUserTag} className="input-icon" />
                                <input
                                    type="text"
                                    id="position"
                                    name="position"
                                    value={formData.position}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="position" className="input-label">
                                    {translations.position || 'Position'}
                                </label>
                            </div>
                        </div>

                        {/* Department */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faBuilding} className="input-icon" />
                                <input
                                    type="text"
                                    id="department"
                                    name="department"
                                    value={formData.department}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="department" className="input-label">
                                    {translations.department || 'Department'}
                                </label>
                            </div>
                        </div>

                        {/* Status */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faTag} className="input-icon" />
                                <select
                                    id="status"
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    disabled={saving || isEditMode}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                >
                                    {statusOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {translations[option.label] || option.displayName}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="status" className="input-label">
                                    {translations.status || 'Status'}
                                </label>
                            </div>
                        </div>

                        {/* Salary Type */}
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faMoneyBillWave} className="input-icon" />
                                <select
                                    id="salary_type"
                                    name="salary_type"
                                    value={formData.salary_type}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                >
                                    {salaryTypeOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {translations[option.label] || option.displayName}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="salary_type" className="input-label">
                                    {translations.salaryType || 'Salary Type'}
                                </label>
                            </div>
                        </div>

                        {/* Conditional Fields based on Salary Type */}
                        {formData.salary_type === 'fixed' && (
                            <div className={`form-group-modern ${errors.base_salary ? 'has-error' : ''}`}>
                                <div className="input-container">
                                    <FontAwesomeIcon icon={faMoneyBillWave} className="input-icon" />
                                    <input
                                        type="number"
                                        id="base_salary"
                                        name="base_salary"
                                        value={formData.base_salary}
                                        onChange={handleChange}
                                        placeholder=" "
                                        disabled={saving}
                                        step="0.01"
                                        min="0"
                                        dir="ltr"
                                        className={errors.base_salary ? 'error' : ''}
                                    />
                                    <label htmlFor="base_salary" className="input-label">
                                        {translations.baseSalary || 'Base Salary'}
                                    </label>
                                    {errors.base_salary && (
                                        <div className="error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {errors.base_salary}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {formData.salary_type === 'hourly' && (
                            <div className={`form-group-modern ${errors.hourly_rate ? 'has-error' : ''}`}>
                                <div className="input-container">
                                    <FontAwesomeIcon icon={faClock} className="input-icon" />
                                    <input
                                        type="number"
                                        id="hourly_rate"
                                        name="hourly_rate"
                                        value={formData.hourly_rate}
                                        onChange={handleChange}
                                        placeholder=" "
                                        disabled={saving}
                                        step="0.01"
                                        min="0"
                                        dir="ltr"
                                        className={errors.hourly_rate ? 'error' : ''}
                                    />
                                    <label htmlFor="hourly_rate" className="input-label">
                                        {translations.hourlyRate || 'Hourly Rate'}
                                    </label>
                                    {errors.hourly_rate && (
                                        <div className="error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {errors.hourly_rate}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {formData.salary_type === 'commission' && (
                            <div className={`form-group-modern ${errors.commission_rate ? 'has-error' : ''}`}>
                                <div className="input-container">
                                    <FontAwesomeIcon icon={faPercentage} className="input-icon" />
                                    <input
                                        type="number"
                                        id="commission_rate"
                                        name="commission_rate"
                                        value={formData.commission_rate}
                                        onChange={handleChange}
                                        placeholder=" "
                                        disabled={saving}
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        dir="ltr"
                                        className={errors.commission_rate ? 'error' : ''}
                                    />
                                    <label htmlFor="commission_rate" className="input-label">
                                        {translations.commissionRate || 'Commission Rate (%)'}
                                    </label>
                                    {errors.commission_rate && (
                                        <div className="error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {errors.commission_rate}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                                                                  
                    
                        {/* Address - Full Width */}
                        <div className="form-group-modern full-width">
                            <div className="input-container textarea-container">
                                <FontAwesomeIcon icon={faMapMarkerAlt} className="input-icon" />
                                <textarea
                                    id="address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    rows="3"
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                ></textarea>
                                <label htmlFor="address" className="input-label">
                                    {translations.address || 'Address'}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="form-actions-modern">
                    <button
                        type="button"
                        className="btn-modern btn-secondary-modern"
                        onClick={handleClear}
                        disabled={saving}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                        <span>{translations.clearButton || 'Clear'}</span>
                    </button>
                    <button
                        type="submit"
                        className="btn-modern btn-primary-modern"
                        disabled={saving || isTranslating || !currentShop || isSubmittingRef.current || submissionLockRef.current}
                    >
                        {saving ? (
                            <>
                                <div className="btn-spinner"></div>
                                <span>{isEditMode ? (translations.updating || 'Updating...') : (translations.adding || 'Adding...')}</span>
                            </>
                        ) : (
                            <>
                                <span>{isEditMode ? (translations.updateEmployee || 'Update Employee') : (translations.saveEmployee || 'Save Employee')}</span>
                                <FontAwesomeIcon icon={faSave} />
                            </>
                        )}
                    </button>
                </div>

                {/* Submission Status Indicator */}
                {(isSubmittingRef.current || submissionLockRef.current) && (
                    <div className="submission-status">
                        <div className="submission-status-text">
                            <FontAwesomeIcon icon={faSpinner} spin />
                            <span>Processing your submission...</span>
                        </div>
                    </div>
                )}
            </form>

            {/* Loading Overlay */}
            {loading && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <div className="loading-spinner"></div>
                        <p>{translations.loading || 'Loading...'}</p>
                        <p className="loading-subtext">Please wait while we load employee data</p>
                    </div>
                </div>
            )}

            {/* Modals */}
            {showHistoryModal && (
                <StatusHistoryModal
                    employeeId={employeeId}
                    shopId={shopId}
                    onClose={() => setShowHistoryModal(false)}
                    translations={translations}
                />
            )}

            {showStatusModal && (
                <StatusChangeModal
                    employee={formData}
                    shopId={shopId}
                    onClose={() => setShowStatusModal(false)}
                    onStatusChanged={(updatedData) => {
                        if (employeeId) {
                            loadEmployeeData(employeeId);
                        }
                        showToast('success', translations.statusChanged || 'Status updated successfully!');
                        loadSummary();
                    }}
                    translations={translations}
                />
            )}
        </div>
    );
};

export default AddEmployee;